//@flow
import * as React from 'react';
import isEqual from 'lodash/isEqual';
import {
    FetchStatusEnum,
    type FetchStatusEnumType
} from 'common/enums/FetchEnum';
import uuidv4 from 'uuid/v4';
import { APIErrorScreen } from 'common/components/core/errors/APIErrorScreen';
import Loader from 'common/components/core/loader/Loader';
import isFunction from 'lodash/isFunction';
import PromiseUtils, { PromiseStatus } from 'common/utils/PromiseUtils';

type WrapProps<TParams: {}> = {
    children?: ({
        status: FetchStatusEnumType,
        onRetry: () => void,
        errors: { [key: string]: Error }
    }) => React.Node,
    params: TParams,
    apiActionsMap: { [key: string]: (params: TParams) => ?Promise<any> }
};

type WrapState = {
    paramsKey: string
};

class MultiAPIFetcher<TParams: {}> extends React.PureComponent<
    WrapProps<TParams>,
    WrapState
> {
    state = {
        paramsKey: uuidv4()
    };
    componentDidUpdate(prevProps: WrapProps<TParams>) {
        if (!isEqual(prevProps.params, this.props.params)) {
            this.setState({ paramsKey: uuidv4() });
        }
    }
    handleRetry = () => {
        this.setState({ paramsKey: uuidv4() });
    };
    render() {
        return (
            <MultiAPIFetcherBase
                {...this.props}
                onRetry={this.handleRetry}
                key={this.state.paramsKey}
            />
        );
    }
}

type Props<TParams> = {
    ...WrapProps<TParams>,
    onRetry: () => void
};

type State = {
    status: FetchStatusEnumType,
    rejectedActionsMap?: ?{ [key: string]: (params: {}) => ?Promise<any> },
    errors: { [key: string]: Error }
};
class MultiAPIFetcherBase<TParams: {}> extends React.PureComponent<
    Props<TParams>,
    State
> {
    state = {
        status: 'LOADING'
    };
    componentDidMount() {
        this._init();
    }
    componentWillUnmount() {
        this.mounted = false;
    }
    mounted = true;

    /**
     * called on first mount, & params change.
     * Does cleanup + triggers a new request
     * */

    _init = () => {
        const { params, apiActionsMap } = this.props;
        this._triggerAPI({ params, apiActionsMap });
    };

    _transitionState = (newState, callback) => {
        this.setState(state => {
            const { status } = state;
            // allow LOADING to SUCESS, FAILURE
            if (
                status === FetchStatusEnum.Loading &&
                ![FetchStatusEnum.Success, FetchStatusEnum.Failure].includes(
                    newState.status
                )
            ) {
                return;
            }
            // allow FAILURE to LOADING
            if (
                status === FetchStatusEnum.Failure &&
                ![FetchStatusEnum.Loading].includes(newState.status)
            ) {
                return;
            }

            return newState;
        }, callback);
    };

    /**
     * called on mount & retry
     * */
    _triggerAPI = ({ params, apiActionsMap }) => {
        this._transitionState({ status: FetchStatusEnum.Loading });
        this._triggerBulk({
            apiActionsMap,
            params,
            onSuccess: () => {
                this._transitionState({
                    status: FetchStatusEnum.Success,
                    rejectedActionsMap: {}
                });
            },
            onFailure: ({ rejectedActionsMap, errors }) => {
                this._transitionState({
                    status: FetchStatusEnum.Failure,
                    errors,
                    rejectedActionsMap
                });
            }
        });
    };

    _triggerBulk = ({ apiActionsMap, params, onSuccess, onFailure }) => {
        this._transitionState({ status: FetchStatusEnum.Loading });

        const promises = {};
        Object.keys(apiActionsMap).forEach(key => {
            promises[key] = apiActionsMap[key](params);
        });
        PromiseUtils.processAll(promises).then(results => {
            const rejectedActionsMap = {};
            const errors = {};
            results.forEach(result => {
                if (result.status === PromiseStatus.REJECTED && result.id) {
                    rejectedActionsMap[result.id] = apiActionsMap[result.id];
                    errors[result.id] = result.error;
                }
            });
            if (Object.keys(rejectedActionsMap).length === 0) {
                // Success
                onSuccess();
                return;
            }
            onFailure({ rejectedActionsMap, errors });
        });
    };

    _handleRetry = () => {
        const { params } = this.props;
        const { rejectedActionsMap } = this.state;
        if (rejectedActionsMap) {
            this._triggerAPI({ params, apiActionsMap: rejectedActionsMap });
        }
    };
    render() {
        const { status, errors } = this.state;
        if (isFunction(this.props.children)) {
            return this.props.children({
                status,
                onRetry: this._handleRetry,
                errors
            });
        }
        if (status === FetchStatusEnum.Failure) {
            return <APIErrorScreen />;
        }
        if (status === FetchStatusEnum.Loading) {
            return <Loader active />;
        }
        return this.props.children;
    }
}

export { MultiAPIFetcher };
export default MultiAPIFetcher;
